import { api, type QueryParams } from "@/lib/api/client";

/**
 * Leaving the company, and the checklist either end of employment carries.
 *
 * One task table covers both phases: a new joiner signs their forms, a leaver
 * answers the exit interview while HR files the settlement papers.
 */

export type SeparationKind = "resignation" | "termination";
export type SeparationStatus = "pending" | "approved" | "rejected" | "withdrawn" | "completed";
export type TaskPhase = "onboarding" | "offboarding";
export type TaskOwner = "employee" | "admin";
export type TaskKind = "sign_document" | "exit_interview" | "upload" | "check";
export type TaskStatus = "pending" | "done" | "skipped";

export interface NoticeTerms {
  notice_days: number;
  submitted_on: string;
  last_working_day: string;
}

export interface Separation {
  id: string;
  user_id: string;
  kind: SeparationKind;
  status: SeparationStatus;
  reason: string | null;
  submitted_on: string;
  notice_days: number;
  last_working_day: string;
  notice_waived: boolean;
  decided_at: string | null;
  decision_note: string | null;
  completed_at: string | null;
  created_at: string;
  employee_name: string | null;
  employee_number: string | null;
  position_title: string | null;
  decided_by_name: string | null;
  /** Days from today to the last working day. Negative once it has passed. */
  days_remaining: number | null;
}

export interface EmployeeTask {
  id: string;
  user_id: string;
  phase: TaskPhase;
  key: string;
  title: string;
  description: string | null;
  owner: TaskOwner;
  kind: TaskKind;
  status: TaskStatus;
  separation_id: string | null;
  document_id: string | null;
  employee_document_id: string | null;
  note: string | null;
  completed_at: string | null;
  sort_order: number;
  completed_by_name: string | null;
  employee_name: string | null;
}

export interface ExitQuestion {
  id: string;
  question: string;
  options: string[];
  sort_order: number;
  is_active: boolean;
}

export interface ExitAnswer {
  question_id: string;
  question: string;
  choice: string;
  comment: string | null;
}

export interface ExitAnswerInput {
  question_id: string;
  choice: string;
  comment?: string | null;
}

export interface ExitQuestionInput {
  question: string;
  options: string[];
}

export interface DecisionInput {
  last_working_day?: string | null;
  note?: string | null;
}

export interface TerminateInput {
  reason: string;
  last_working_day?: string | null;
}

export const separationKeys = {
  all: ["separation"] as const,
  noticeTerms: () => [...separationKeys.all, "notice-terms"] as const,
  mine: () => [...separationKeys.all, "mine"] as const,
  myTasks: () => [...separationKeys.all, "my-tasks"] as const,
  exitForm: () => [...separationKeys.all, "exit-form"] as const,
  list: (status?: string) => [...separationKeys.all, "list", status ?? "all"] as const,
  employeeTasks: (userId: string) => [...separationKeys.all, "employee-tasks", userId] as const,
  openTasks: (filters: Record<string, unknown>) =>
    [...separationKeys.all, "open-tasks", filters] as const,
  exitAnswers: (separationId: string) =>
    [...separationKeys.all, "exit-answers", separationId] as const,
  questions: () => [...separationKeys.all, "questions"] as const,
  joiningForms: () => [...separationKeys.all, "joining-forms"] as const,
  joiningFormPeople: (key: string) =>
    [...separationKeys.all, "joining-forms", key, "people"] as const,
};

/** One of the forms every joiner signs, as the company has it now. */
export interface JoiningForm {
  key: string;
  name: string;
  description: string | null;
  /** The form drawn blank, to read. Each person signs their own copy. */
  file_id: string | null;
  /** The wording, which HR can edit; empty for a form that is an uploaded PDF. */
  body_text: string | null;
  editable: boolean;
  version: number;
  text_updated_at: string | null;
  text_updated_by_name: string | null;
  /** Active staff who signed the current version, have it waiting, or signed only an earlier one. */
  signed_current: number;
  waiting_current: number;
  signed_older: number;
}

/** A form's new wording, saved: whether it changed and so is a new version. */
export interface JoiningFormSaved {
  form: JoiningForm;
  changed: boolean;
}

/** Somebody active, and where they stand with one form. */
export interface JoiningFormPerson {
  user_id: string;
  full_name: string;
  employee_number: string | null;
  position: string | null;
  /** For the latest version they were sent: signed, waiting, or never sent. */
  state: "signed" | "waiting" | "none";
  version: number | null;
  current: boolean;
}

/** What sending the current forms did. */
export interface JoiningFormsSent {
  people: number;
  /** Old forms still unsigned, taken back. */
  withdrawn: number;
  sent: number;
}

export const separationApi = {
  noticeTerms: () => api.get<NoticeTerms>("/me/notice-terms"),

  mine: () => api.get<Separation | null>("/me/separation"),

  resign: (reason?: string) =>
    api.post<Separation>("/me/separation", { reason: reason?.trim() || null }),

  withdraw: () => api.post<Separation>("/me/separation/withdraw"),

  myTasks: () => api.get<EmployeeTask[]>("/me/tasks"),

  mySeparationTasks: () => api.get<EmployeeTask[]>("/me/separation/tasks"),

  exitForm: () => api.get<ExitQuestion[]>("/me/exit-interview"),

  submitExitInterview: (answers: ExitAnswerInput[]) =>
    api.post<{ message: string }>("/me/exit-interview", { answers }),

  list: (status?: string) =>
    api.get<Separation[]>("/separations", { query: status ? { status } : undefined }),

  terminate: (userId: string, payload: TerminateInput) =>
    api.post<Separation>(`/employees/${userId}/terminate`, payload),

  approve: (id: string, payload: DecisionInput = {}) =>
    api.post<Separation>(`/separations/${id}/approve`, {
      last_working_day: payload.last_working_day || null,
      note: payload.note?.trim() || null,
    }),

  reject: (id: string, note: string) => api.post<Separation>(`/separations/${id}/reject`, { note }),

  exitAnswers: (separationId: string) =>
    api.get<ExitAnswer[]>(`/separations/${separationId}/exit-interview`),

  employeeTasks: (userId: string) => api.get<EmployeeTask[]>(`/employees/${userId}/tasks`),

  joiningForms: () => api.get<JoiningForm[]>("/joining-forms"),

  sendJoiningForms: (userId: string) =>
    api.post<JoiningFormsSent>(`/employees/${userId}/joining-forms/send`),

  sendJoiningFormsToEveryone: () =>
    api.post<JoiningFormsSent>("/joining-forms/send-to-everyone"),

  saveJoiningForm: (key: string, bodyText: string) =>
    api.put<JoiningFormSaved>(`/joining-forms/${key}`, { body_text: bodyText }),

  joiningFormPeople: (key: string) =>
    api.get<JoiningFormPerson[]>(`/joining-forms/${key}/people`),

  sendJoiningForm: (key: string, choice: { userIds: string[] } | { everyone: true }) =>
    api.post<JoiningFormsSent>(
      `/joining-forms/${key}/send`,
      "everyone" in choice ? { everyone: true } : { user_ids: choice.userIds },
    ),

  openTasks: (filters: QueryParams = {}) => api.get<EmployeeTask[]>("/tasks", { query: filters }),

  completeTask: (id: string, note?: string) =>
    api.post<EmployeeTask>(`/tasks/${id}/complete`, { note: note?.trim() || null }),

  uploadForTask: (id: string, file: File, note?: string) => {
    const body = new FormData();
    body.append("file", file);
    if (note?.trim()) body.append("note", note.trim());
    return api.post<EmployeeTask>(`/tasks/${id}/upload`, body);
  },

  reopenTask: (id: string) => api.post<EmployeeTask>(`/tasks/${id}/reopen`),

  questions: () => api.get<ExitQuestion[]>("/exit-interview/questions"),

  setQuestions: (questions: ExitQuestionInput[]) =>
    api.put<ExitQuestion[]>("/exit-interview/questions", { questions }),
};
